// packages/ui/src/components/chat/preChatFormResolver.ts
//
// The pre-chat form's validation, without zod: the form was the only zod user the
// chat widget bundled (docs/embed-size-and-speed.md §4, chat-prechat-resolver).
// It answers react-hook-form exactly as `zodResolver` did with the form's former
// schema, which preChatFormResolver.test.ts keeps as the reference:
//
//   name, phone.countryCode, phone.number, message: trimmed, at least one character;
//   email:   untrimmed, zod 4.3's email pattern (`z.email()`);
//   message: trimmed, at most `messageMaxLength` characters.
//
// The messages are translation keys, which `Form` and the fields translate.
import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers';
import type { FieldError, Resolver, ResolverOptions, ResolverResult } from 'react-hook-form';

export interface PreChatFormValues {
  name: string;
  email: string;
  phone: { countryCode: string; number: string };
  message: string;
}

/** zod 4.3's `z.email()` pattern (zod/v4/core/regexes.js `email`), copied verbatim. */
// eslint-disable-next-line no-useless-escape -- zod's own text, kept comparable with zod's source
const EMAIL_PATTERN = /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-\.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;

/** The issue codes zod reported, which react-hook-form keeps as each error's `type`. */
const REQUIRED: FieldError = { message: 'Please_fill_out_this_field', type: 'too_small' };
const TOO_LONG: FieldError = { message: 'This_field_is_too_long', type: 'too_big' };
const EMAIL_INVALID: FieldError = { message: 'Email_invalid', type: 'invalid_format' };

export function preChatFormResolver(messageMaxLength: number): Resolver<PreChatFormValues> {
  // It answers with a promise, as zodResolver did.
  return (values, _context, options) => Promise.resolve(answer(values, options, messageMaxLength));
}

function answer(
  values: PreChatFormValues,
  options: ResolverOptions<PreChatFormValues>,
  messageMaxLength: number,
): ResolverResult<PreChatFormValues> {
  const parsed: PreChatFormValues = {
    name: values.name.trim(),
    email: values.email,
    phone: { countryCode: values.phone.countryCode.trim(), number: values.phone.number.trim() },
    message: values.message.trim(),
  };
  // In the fields' order, one error per field (the form's criteriaMode is 'firstError'). New objects each time:
  // toNestErrors writes each field's `ref` into its error.
  const errors: Record<string, FieldError> = {};
  if (parsed.name.length === 0) errors.name = { ...REQUIRED };
  if (!EMAIL_PATTERN.test(parsed.email)) errors.email = { ...EMAIL_INVALID };
  if (parsed.phone.countryCode.length === 0) errors['phone.countryCode'] = { ...REQUIRED };
  if (parsed.phone.number.length === 0) errors['phone.number'] = { ...REQUIRED };
  if (parsed.message.length === 0) errors.message = { ...REQUIRED };
  else if (parsed.message.length > messageMaxLength) errors.message = { ...TOO_LONG };

  if (Object.keys(errors).length > 0) return { values: {}, errors: toNestErrors(errors, options) };
  if (options.shouldUseNativeValidation) validateFieldsNatively({}, options);
  return { errors: {}, values: parsed };
}
